import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Web3Auth, WEB3AUTH_NETWORK, CHAIN_NAMESPACES } from "@web3auth/modal";
import type { IProvider } from "@web3auth/modal";
import RPC from "./viemRPC";
import { requireEnv } from "../config/env";
import { signInToFirebaseWithWeb3Auth, signOutFirebase } from "../firebase/firebaseAuth";

const clientId = requireEnv('VITE_WEB3AUTH_CLIENT_ID');

const baseChainConfig = {
  chainNamespace: CHAIN_NAMESPACES.EIP155,
  chainId: "0x2105", // hex of 8453
  rpcTarget: "https://mainnet.base.org",
  displayName: "Base",
  blockExplorerUrl: "https://basescan.org",
  ticker: "ETH",
  tickerName: "ETH",
  logo: "https://images.toruswallet.io/base.svg",
};

interface Web3AuthContextType {
  web3auth: Web3Auth | null;
  provider: IProvider | null;
  isLoggedIn: boolean;
  isRestoringSession: boolean;
  isFirebaseReady: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  getUserInfo: () => Promise<any>;
  getAddress: () => Promise<string | null>;
  isSessionReady: () => Promise<{ ready: boolean; reason?: string; provider: IProvider | null }>;
  isInitialized: boolean;
  isLoading: boolean;
}

const Web3AuthContext = createContext<Web3AuthContextType | null>(null);

export const Web3AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [web3auth, setWeb3auth] = useState<Web3Auth | null>(null);
  const [provider, setProvider] = useState<IProvider | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isRestoringSession, setIsRestoringSession] = useState<boolean>(true);
  const [isFirebaseReady, setIsFirebaseReady] = useState<boolean>(false);
  const [isLoading] = useState(false);

  const establishFirebaseSession = async (instance: Web3Auth) => {
    const tokenInfo = await instance.getIdentityToken();
    const idToken = tokenInfo?.idToken;
    if (!idToken) {
      throw new Error("Web3Auth did not return an ID token");
    }
    await signInToFirebaseWithWeb3Auth(idToken);
    setIsFirebaseReady(true);
  };

  const syncSessionState = (
    activeWeb3Auth: Web3Auth | null,
    nextProvider: IProvider | null,
    reason: string
  ) => {
    const sdkConnected = Boolean(activeWeb3Auth?.connected);
    const sdkStatus = activeWeb3Auth?.status;
    const hasProvider = Boolean(nextProvider);
    const shouldBeLoggedIn = sdkConnected && sdkStatus === 'connected' && hasProvider;
    setProvider(nextProvider);
    setIsLoggedIn(shouldBeLoggedIn);
    if (!shouldBeLoggedIn) {
      console.warn('Web3Auth session not ready:', {
        reason,
        sdkConnected,
        sdkStatus,
        hasProvider,
      });
    }
  };

  const isSessionReady = async (): Promise<{ ready: boolean; reason?: string; provider: IProvider | null }> => {
    const activeWeb3Auth = web3auth;
    if (!activeWeb3Auth) {
      return { ready: false, reason: 'Web3Auth is not initialized', provider: null };
    }

    const activeProvider = activeWeb3Auth.provider || provider;
    const sdkConnected = Boolean(activeWeb3Auth.connected);
    const sdkStatus = activeWeb3Auth.status;
    const ready = sdkConnected && sdkStatus === 'connected' && Boolean(activeProvider);

    if (!ready) {
      syncSessionState(activeWeb3Auth, activeProvider, 'isSessionReady-check');
      return {
        ready: false,
        reason: 'Session expired or disconnected. Please sign in again.',
        provider: activeProvider || null,
      };
    }

    if (!provider && activeProvider) {
      setProvider(activeProvider);
    }
    if (!isLoggedIn) {
      setIsLoggedIn(true);
    }

    return { ready: true, provider: activeProvider || null };
  };

  const ensureBaseChain = async (activeProvider: IProvider) => {
    try {
      await activeProvider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: baseChainConfig.chainId }],
      });
    } catch (switchError: any) {
      if (switchError.code === 4902) {
        await activeProvider.request({
          method: "wallet_addEthereumChain",
          params: [{
            chainId: baseChainConfig.chainId,
            chainName: baseChainConfig.displayName,
            rpcUrls: [baseChainConfig.rpcTarget],
            blockExplorerUrls: [baseChainConfig.blockExplorerUrl],
            nativeCurrency: {
              name: "Ethereum",
              symbol: "ETH",
              decimals: 18,
            },
          }],
        });
      }
    }
  };

  const shouldAttemptGuardarianRecovery = () => {
    if (typeof window === 'undefined') return false;

    const search = window.location.search || '';
    const hasGuardarianQuery = search.includes('guardarian=');
    const hasTidQuery = search.includes('tid=');
    const pathname = window.location.pathname;
    const isGuardarianRoute = pathname === '/withdraw' || pathname === '/send' || pathname === '/deposit';

    if (hasGuardarianQuery || (isGuardarianRoute && hasTidQuery)) {
      return true;
    }

    try {
      const withdrawInProgress = localStorage.getItem('withdrawInProgress') === '1';
      const pendingGuardarianWithdraw = Boolean(localStorage.getItem('pendingGuardarianWithdraw'));
      // Deposit returns set guardarianReturnIntent before the full-page redirect.
      // On an early exit, Guardarian's Close does a history.back() to /TabNavigator
      // with no query param, so this is the only surviving marker that we need to
      // reconnect the session instead of bouncing to SignIn.
      const pendingDepositReturn = localStorage.getItem('guardarianReturnIntent') === 'deposit';
      return withdrawInProgress || pendingGuardarianWithdraw || pendingDepositReturn;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    const initE2E = async () => {
      // TEST ONLY: bypasses the real Web3Auth modal/SDK entirely so headless
      // E2E runs can authenticate as a fixture persona. Dynamically imported
      // so this module (and its personas.json fixture data) is dead code
      // and never bundled unless VITE_E2E is explicitly set to '1'.
      const { createE2EWeb3Auth, selectE2EPersona } = await import('./e2e/web3AuthE2E');
      const persona = selectE2EPersona();
      const fakeWeb3Auth = createE2EWeb3Auth(persona) as unknown as Web3Auth;

      syncSessionState(fakeWeb3Auth, fakeWeb3Auth.provider, 'e2e-init');

      if (import.meta.env.VITE_E2E_SKIP_FIREBASE_AUTH === '1') {
        setIsFirebaseReady(true);
      } else {
        try {
          await establishFirebaseSession(fakeWeb3Auth);
        } catch (firebaseError) {
          console.error("Failed to establish Firebase session (E2E):", firebaseError);
        }
      }

      setWeb3auth(fakeWeb3Auth);
      setIsInitialized(true);
    };

    const init = async () => {
      try {
        setIsRestoringSession(true);

        if (import.meta.env.VITE_E2E === '1') {
          await initE2E();
          return;
        }

        const web3auth = new Web3Auth({
          clientId,
          web3AuthNetwork: WEB3AUTH_NETWORK.SAPPHIRE_MAINNET,
          chains: [baseChainConfig],
          defaultChainId: "0x2105",
          enableLogging: import.meta.env.DEV,
        });

        await web3auth.init();

        let activeProvider = web3auth.provider;

        // During off-ramp callbacks, attempt one reconnect before deciding to redirect.
        if (!activeProvider && shouldAttemptGuardarianRecovery()) {
          try {
            activeProvider = await web3auth.connect();
          } catch (error) {
            console.warn("Guardarian resume session reconnect failed:", error);
          } finally {
            // connect() uses the modal SDK, which renders its "connecting" UI even
            // for a silent cached-session reconnect. There's no headless reconnect
            // mode, so defensively close the modal the moment connect() settles to
            // minimise the flash on the Guardarian return path (mirrors login()).
            try {
              (web3auth as unknown as { loginModal?: { closeModal?: () => void } })
                .loginModal?.closeModal?.();
            } catch (closeError) {
              console.warn("Failed to close Web3Auth modal after recovery reconnect:", closeError);
            }
          }
        }

        // A deposit early-exit (Guardarian "Close" → history.back to /TabNavigator)
        // returns with no `guardarian=` query, so Deposit.tsx never clears the
        // deposit return marker. Clear it here once it has served the reconnect
        // above, so it can't force an unwanted connect() on a later cold load.
        try {
          const search = typeof window !== 'undefined' ? window.location.search || '' : '';
          if (!search.includes('guardarian=') && localStorage.getItem('guardarianReturnIntent') === 'deposit') {
            localStorage.removeItem('guardarianReturnIntent');
          }
        } catch {
          // no-op: localStorage may be unavailable
        }

        if (activeProvider) {
          syncSessionState(web3auth, activeProvider, 'init');
          await ensureBaseChain(activeProvider);
          try {
            await establishFirebaseSession(web3auth);
          } catch (firebaseError) {
            console.error("Failed to establish Firebase session on restore:", firebaseError);
          }
        } else {
          syncSessionState(web3auth, null, 'init-no-provider');
        }

        setWeb3auth(web3auth);
        setIsInitialized(true);
      } catch (error) {
        console.error("Failed to initialize Web3Auth:", error);
        setIsInitialized(true);
      } finally {
        setIsRestoringSession(false);
      }
    };

    init();
  }, []);

  const login = async () => {
    if (!web3auth) {
      return;
    }
    try {
      const web3authProvider = await web3auth.connect();
      syncSessionState(web3auth, web3authProvider, 'login');

      // On mobile, the Web3Auth modal sometimes stays visible after connect()
      // resolves because the in-modal "Connected" loader is unmounted before its
      // auto-close timeout fires. Defensively close it.
      try {
        (web3auth as unknown as { loginModal?: { closeModal?: () => void } })
          .loginModal?.closeModal?.();
      } catch (closeError) {
        console.warn("Failed to close Web3Auth modal after login:", closeError);
      }

      await establishFirebaseSession(web3auth);
    } catch (error) {
      console.error("Error logging in:", error);
      throw error;
    }
  };

  const logout = async () => {
    if (!web3auth) {
      return;
    }
    await web3auth.logout();
    syncSessionState(web3auth, null, 'logout');
    try {
      await signOutFirebase();
    } catch (firebaseError) {
      console.error("Failed to sign out of Firebase:", firebaseError);
    }
    setIsFirebaseReady(false);
  };

  const getUserInfo = async () => {
    if (!web3auth) {
      return null;
    }
    const user = await web3auth.getUserInfo();
    return user;
  };

  const getAddress = async () => {
    // Prefer React state provider, but fall back to web3auth.provider
    // to handle the race where login() set state but React hasn't re-rendered yet
    const activeProvider = provider || web3auth?.provider;
    if (!activeProvider) {
      return null;
    }
    const rpc = new RPC(activeProvider);
    const address = await rpc.getAccounts();
    return address[0];
  };

  const value = {
    web3auth,
    provider,
    isLoggedIn,
    isRestoringSession,
    isFirebaseReady,
    login,
    logout,
    getUserInfo,
    getAddress,
    isSessionReady,
    isInitialized,
    isLoading,
  };

  return <Web3AuthContext.Provider value={value}>{children}</Web3AuthContext.Provider>;
};

export const useWeb3Auth = () => {
  const context = useContext(Web3AuthContext);
  if (!context) {
    throw new Error('useWeb3Auth must be used within a Web3AuthProvider');
  }
  return context;
};
